import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  FilterDimension,
  FilterValue,
  setAvailableValues,
  clearFilter,
  setLoading,
  setError,
  updateRecordCounts,
} from '../store/enhancedFiltersSlice';
import FilterService from '../services/FilterService';

/**
 * Hook to manage filter dependencies and cascading updates
 */
export const useFilterDependencies = () => {
  const dispatch = useDispatch();
  const activeFilters = useSelector((state: RootState) => state.enhancedFilters.activeFilters);
  const dependencies = useSelector((state: RootState) => state.enhancedFilters.dependencies);
  const currentRole = useSelector((state: RootState) => state.user.currentRole);

  /**
   * Update available values for dependent filters when parent changes
   */
  const updateDependentFilters = useCallback(async (
    parentDimension: FilterDimension,
    parentValues: string[]
  ) => {
    const dependentDimensions = dependencies[parentDimension];
    if (!dependentDimensions) return;

    for (const dependentDimension of dependentDimensions) {
      try {
        dispatch(setLoading(true));
        
        // Get updated available values for dependent filter
        const availableValues = await FilterService.getAvailableValues(
          dependentDimension,
          currentRole,
          { [parentDimension]: parentValues }
        );

        // Update the available values in the store
        dispatch(setAvailableValues({
          dimension: dependentDimension,
          values: availableValues
        }));

        // Check if current selection is still valid
        const currentSelection = activeFilters[dependentDimension]?.values || [];
        const validSelection = currentSelection.filter((value: any) =>
          availableValues.some(av => av.id === value && !av.disabled)
        );

        // Clear the filter if no valid selections remain
        if (currentSelection.length > 0 && validSelection.length === 0) {
          dispatch(clearFilter(dependentDimension));
        }

      } catch (error) {
        console.error(`Error updating dependent filter ${dependentDimension}:`, error);
        dispatch(setError(`Failed to update ${dependentDimension} filter options`));
      } finally {
        dispatch(setLoading(false));
      }
    }
  }, [dependencies, activeFilters, currentRole, dispatch]);

  /**
   * Update filtered record count when filters change
   */
  const updateFilteredCount = useCallback(async () => {
    try {
      const filterValues = Object.entries(activeFilters).reduce((acc, [dimension, filter]) => {
        if (filter && (filter as any).values?.length > 0) {
          acc[dimension as FilterDimension] = (filter as any).values;
        }
        return acc;
      }, {} as Partial<Record<FilterDimension, string[]>>);

      const totalCount = await FilterService.getFilteredCount(filterValues, currentRole);
      
      // For demo purposes, calculate a mock filtered count
      const filteredCount = Math.max(
        Math.floor(totalCount * (1 - Object.keys(filterValues).length * 0.15)),
        Object.keys(filterValues).length > 0 ? 5 : totalCount
      );

      dispatch(updateRecordCounts({
        total: totalCount,
        filtered: filteredCount
      }));
    } catch (error) {
      console.error('Error updating filtered count:', error);
    }
  }, [activeFilters, currentRole, dispatch]);

  /**
   * Watch for filter changes and update dependencies
   */
  useEffect(() => {
    const filterEntries = Object.entries(activeFilters);
    
    filterEntries.forEach(([dimension, filter]) => {
      if (filter && (filter as any).values?.length > 0) {
        updateDependentFilters(dimension as FilterDimension, (filter as any).values);
      }
    });

    // Update the filtered count
    updateFilteredCount();
  }, [activeFilters, updateDependentFilters, updateFilteredCount]);

  /**
   * Load initial available values for all filters
   */
  const loadInitialFilterValues = useCallback(async () => {
    const dimensions: FilterDimension[] = [
      'country', 'businessUnit', 'department', 'level', 
      'manager', 'team', 'gender', 'performanceBand', 'attritionRiskBand'
    ];

    try {
      dispatch(setLoading(true));

      for (const dimension of dimensions) {
        const values = await FilterService.getAvailableValues(
          dimension,
          currentRole
        );
        
        dispatch(setAvailableValues({
          dimension,
          values
        }));
      }

      // Set initial record counts
      const totalCount = await FilterService.getFilteredCount({}, currentRole);
      dispatch(updateRecordCounts({
        total: totalCount,
        filtered: totalCount
      }));

    } catch (error) {
      console.error('Error loading initial filter values:', error);
      dispatch(setError('Failed to load filter options'));
    } finally {
      dispatch(setLoading(false));
    }
  }, [currentRole, dispatch]);

  /**
   * Validate filter selection against dependencies
   */
  const validateFilterSelection = useCallback((
    dimension: FilterDimension,
    value: string
  ): boolean => {
    // Check if parent filters are set when required
    const parentDimensions = Object.entries(dependencies)
      .filter(([_, deps]) => (deps as any)?.includes(dimension))
      .map(([parent]) => parent as FilterDimension);

    for (const parent of parentDimensions) {
      const parentFilter = activeFilters[parent];
      if (!parentFilter || (parentFilter as any).values?.length === 0) {
        // Parent filter not set - this might be okay depending on business rules
        console.warn(`Parent filter ${parent} not set for ${dimension}`);
      }
    }

    return true; // For now, always allow selection
  }, [dependencies, activeFilters]);

  /**
   * Get cascading filter path
   */
  const getFilterPath = useCallback((dimension: FilterDimension): FilterDimension[] => {
    const path: FilterDimension[] = [dimension];
    
    // Find all ancestors
    let current = dimension;
    const visited = new Set<FilterDimension>();
    
    while (true) {
      if (visited.has(current)) break;
      visited.add(current);
      
      const parent = Object.entries(dependencies).find(([_, deps]) =>
        (deps as any)?.includes(current)
      );
      
      if (parent) {
        path.unshift(parent[0] as FilterDimension);
        current = parent[0] as FilterDimension;
      } else {
        break;
      }
    }
    
    return path;
  }, [dependencies]);

  /**
   * Clear filters in cascade
   */
  const clearCascadingFilters = useCallback((dimension: FilterDimension) => {
    dispatch(clearFilter(dimension));
    
    // Clear all dependent filters
    const dependents = dependencies[dimension];
    if (dependents) {
      dependents.forEach((dep: any) => {
        dispatch(clearFilter(dep));
        // Recursively clear dependents of dependents
        const subDependents = (dependencies as any)[dep];
        if (subDependents) {
          subDependents.forEach((subDep: any) => dispatch(clearFilter(subDep)));
        }
      });
    }
  }, [dependencies, dispatch]);

  return {
    loadInitialFilterValues,
    validateFilterSelection,
    getFilterPath,
    clearCascadingFilters,
    updateDependentFilters,
    updateFilteredCount,
  };
};

/**
 * Hook to manage cross-filtering from widgets
 */
export const useCrossFiltering = () => {
  const dispatch = useDispatch();
  const crossFilterSource = useSelector((state: RootState) => 
    state.enhancedFilters.crossFilterSource
  );

  /**
   * Apply cross-filter from a widget
   */
  const applyCrossFilter = useCallback((
    widgetId: string,
    dimension: FilterDimension,
    value: string
  ) => {
    dispatch({
      type: 'enhancedFilters/setCrossFilter',
      payload: { widgetId, dimension, value }
    });
  }, [dispatch]);

  /**
   * Clear cross-filter
   */
  const clearCrossFilter = useCallback(() => {
    dispatch({ type: 'enhancedFilters/clearCrossFilter' });
  }, [dispatch]);

  /**
   * Check if a widget is the source of current cross-filter
   */
  const isFilterSource = useCallback((widgetId: string): boolean => {
    return crossFilterSource?.widgetId === widgetId;
  }, [crossFilterSource]);

  return {
    crossFilterSource,
    applyCrossFilter,
    clearCrossFilter,
    isFilterSource,
  };
};